'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AlertNotificationBadge from '@/components/common/AlertNotificationBadge';

interface Alert {
  id: string;
  type: 'medication' | 'checkin' | 'benefit' | 'document';
  title: string;
  description: string;
  timestamp: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionUrl: string;
  actionLabel: string;
}

interface UrgentAlertsProps {
  alerts: Alert[];
}

const UrgentAlerts = ({ alerts }: UrgentAlertsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'medication':
        return 'BeakerIcon';
      case 'checkin':
        return 'PhoneIcon';
      case 'benefit':
        return 'GiftIcon';
      case 'document':
        return 'DocumentTextIcon';
      default:
        return 'BellIcon';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-error bg-error/5';
      case 'high':
        return 'border-warning bg-warning/5';
      case 'medium':
        return 'border-accent bg-accent/5';
      default:
        return 'border-primary bg-primary/5';
    }
  };

  const handleDismiss = (alertId: string) => {
    setDismissedAlerts([...dismissedAlerts, alertId]);
  };

  const visibleAlerts = alerts.filter(
    (alert) => !dismissedAlerts.includes(alert.id)
  );

  if (!isHydrated) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="h-24 bg-muted rounded-lg animate-pulse"
          ></div>
        ))}
      </div>
    );
  }

  if (visibleAlerts.length === 0) {
    return (
      <div className="p-6 bg-success/5 border border-success/20 rounded-lg text-center">
        <Icon
          name="CheckCircleIcon"
          size={48}
          className="text-success mx-auto mb-3"
        />
        <p className="text-sm font-caption text-success font-medium">
          All caught up! No urgent alerts at this time.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {visibleAlerts.map((alert) => (
        <div
          key={alert.id}
          className={`p-4 border-l-4 rounded-lg transition-smooth hover:shadow-warm ${getPriorityColor(
            alert.priority
          )}`}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <Icon
                name={getAlertIcon(alert.type) as any}
                size={24}
                className={
                  alert.priority === 'urgent' ?'text-error'
                    : alert.priority === 'high' ?'text-warning' :'text-accent'
                }
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="text-base font-caption font-semibold text-foreground">
                  {alert.title}
                </h3>
                <AlertNotificationBadge
                  count={1}
                  priority={alert.priority}
                  showPulse={alert.priority === 'urgent'}
                />
              </div>
              <p className="text-sm font-caption text-muted-foreground mb-3">
                {alert.description}
              </p>
              <div className="flex items-center gap-3">
                <Link
                  href={alert.actionUrl}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-caption font-medium hover:bg-primary/90 transition-smooth"
                >
                  {alert.actionLabel}
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <button
                  onClick={() => handleDismiss(alert.id)}
                  className="px-4 py-2 text-sm font-caption font-medium text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UrgentAlerts;